/**
 * src/lib/consent/consent.ts
 *
 * KVKK/GDPR çerez onayı — durum tipi + localStorage/cookie okuma-yazma.
 *
 * 3 kategori: necessary (her zaman, zorunlu çerezler — session/CSRF/onay tercihi),
 * analytics (GA4), marketing (3rd-party piksel — şu an yok, ileride). Onay
 * verilmeden analytics/marketing çerezi YÜKLENMEZ (kvkk-compliance.md).
 *
 * Saklama: localStorage (`secsys_consent_v1`) + cookie (SSR/middleware okuyabilsin
 * diye, 1 yıl). Versiyon artarsa eski onay geçersiz → banner tekrar gösterilir.
 */

/** localStorage + cookie anahtarı. Versiyon bump'ında değiştir (re-prompt). */
export const CONSENT_KEY = 'secsys_consent_v1'

/** Onay şeması versiyonu. Politika değişiminde artır → eski onaylar geçersiz. */
export const CONSENT_VERSION = 1

/** Cookie ömrü — 1 yıl (saniye). */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

/**
 * Çerez onay durumu. necessary her zaman true (zorunlu çerezler onay gerektirmez).
 */
export interface ConsentState {
  version: number
  necessary: true
  analytics: boolean
  marketing: boolean
  /** ISO timestamp — onayın verildiği an (proof-of-consent). */
  timestamp: string
  /** Onay kaynağı — banner ana butonu mu, özelleştirme paneli mi. */
  source: 'banner' | 'preferences'
}

/**
 * readConsent — saklı onayı okur (önce localStorage, yoksa cookie). Versiyon
 * uyuşmazsa veya parse hatası varsa null (karar verilmemiş → banner gösterilir).
 *
 * Sadece client'ta çalışır (window/document gerekir); SSR'da null döner.
 */
export function readConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw =
      window.localStorage.getItem(CONSENT_KEY) ?? readCookie(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ConsentState>
    if (parsed.version !== CONSENT_VERSION) return null
    return {
      version: CONSENT_VERSION,
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      timestamp: typeof parsed.timestamp === 'string' ? parsed.timestamp : '',
      source: parsed.source === 'preferences' ? 'preferences' : 'banner',
    }
  } catch {
    return null
  }
}

/**
 * writeConsent — onayı localStorage + cookie'ye yazar.
 * @param state Yazılacak onay durumu (timestamp çağıran tarafça set edilir)
 */
export function writeConsent(state: ConsentState): void {
  if (typeof window === 'undefined') return
  const json = JSON.stringify(state)
  try {
    window.localStorage.setItem(CONSENT_KEY, json)
  } catch {
    // localStorage kapalı (private mode) — cookie fallback yine de yazılır.
  }
  // SameSite=Lax: aynı-site navigasyonda gönderilir; cross-site POST'ta değil.
  document.cookie = `${CONSENT_KEY}=${encodeURIComponent(json)}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`
}

/** Ham cookie değerini okur (localStorage fallback). */
function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`),
  )
  return match?.[1] ? decodeURIComponent(match[1]) : null
}
